<template>
  <div class="wv-rail" :style="{ '--wv-map-height': `${workflow.map.viewBox.split(/\s+/)[3]}px` }" @wheel="onWheel">
    <div class="wv-rail-head">
      <div class="wv-rail-title">{{ workflow.map.title }}</div>
      <div class="wv-legend">
        <span
          v-for="label in workflow.map.legend"
          :key="label"
          class="wv-legend-item"
          :class="`wv-role-${legendRole(label)}`"
        >
          <i />{{ label }}
        </span>
      </div>
    </div>
    <div ref="mapViewport" class="wv-rail-body">
      <svg ref="mapSvg" class="wv-map" :viewBox="workflow.map.viewBox" xmlns="http://www.w3.org/2000/svg" aria-label="Workflow map">
        <template v-for="(track, index) in workflow.map.tracks">
          <path :key="`base-${index}`" class="wv-track-base" :d="track.d" :style="trackStyle(index, true)" />
          <path
            :key="`progress-${index}`"
            class="wv-track-progress"
            :class="`wv-role-${trackRole(track)}`"
            :d="track.d"
            :style="trackStyle(index)"
          />
        </template>

        <g
          v-for="(node, index) in workflow.map.nodes"
          :key="`node-${index}`"
          class="wv-station"
          :class="[{ reached: node.step <= progress }, `wv-role-${node.role}`]"
          role="button"
          tabindex="0"
          :aria-label="`${node.label}, step ${node.step}`"
          @click="$emit('select-step', node.step)"
          @keydown.enter.prevent="$emit('select-step', node.step)"
          @keydown.space.prevent="$emit('select-step', node.step)"
        >
          <circle class="wv-node" :cx="node.x" :cy="node.y" r="8.5" />
          <text class="wv-node-label" :x="node.labelX" :y="node.labelY" :text-anchor="node.anchor || 'start'">
            {{ node.label }}
          </text>
        </g>

        <g
          class="wv-atom wv-role-all"
          :class="{ 'is-hidden': !visible.all, waiting: marker.all[2] }"
          :transform="translate(marker.all)"
        >
          <circle class="wv-halo" r="18" />
          <circle class="wv-ring" r="11.5" />
          <circle class="wv-core" r="6" />
        </g>
        <g
          class="wv-atom wv-role-employee"
          :class="{ 'is-hidden': !visible.employee, waiting: marker.employee[2] }"
          :transform="translate(marker.employee)"
        >
          <circle class="wv-halo" r="18" />
          <circle class="wv-ring" r="11.5" />
          <circle class="wv-core" r="6" />
        </g>
        <g
          class="wv-atom wv-role-contractor"
          :class="{ 'is-hidden': !visible.contractor, waiting: marker.contractor[2] }"
          :transform="translate(marker.contractor)"
        >
          <circle class="wv-halo" r="18" />
          <circle class="wv-ring" r="11.5" />
          <circle class="wv-core" r="6" />
        </g>
      </svg>
    </div>
    <div class="wv-rail-foot">{{ workflow.map.footer }}</div>
  </div>
</template>

<script>
import WorkflowMapMixin from './mixins/WorkflowMapMixin'

export default {
  name: 'WorkflowMap',
  props: {
    workflow: { type: Object, required: true },
    activeStep: { type: Number, required: true },
    progress: { type: Number, required: true }
  },
  mixins: [WorkflowMapMixin]
}
</script>

<style scoped>
.wv-rail {
  position: relative;
  z-index: 2;
  grid-column: 1;
  grid-row: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  border-right: 1px solid var(--border-color-base);
  background: var(--bg-body);
}

.wv-rail-head {
  padding: 24px 24px 20px;
  border-bottom: 1px solid var(--border-color-split);
}

.wv-rail-title {
  margin-bottom: 14px;
  color: var(--text-color-alpha4);
  font-size: 10px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
}

.wv-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.wv-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 10px;
  border: 1px solid var(--border-color-base);
  border-radius: 3px;
  font-size: 10px;
  letter-spacing: 0.06em;
}

.wv-legend-item i {
  display: block;
  flex-shrink: 0;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}

.wv-role-all {
  --wv-role: var(--wv-all);
  color: var(--wv-all);
}
.wv-role-employee {
  --wv-role: var(--wv-employee);
  color: var(--wv-employee);
}
.wv-role-contractor {
  --wv-role: var(--wv-contractor);
  color: var(--wv-contractor);
}

.wv-rail-body {
  position: relative;
  flex: 1;
  min-height: 0;
}

.wv-map {
  position: absolute;
  top: 2px;
  left: 0;
  width: 100%;
  height: calc(100% - 4px);
  overflow: visible;
}

.wv-track-base,
.wv-track-progress {
  fill: none;
}
.wv-track-base {
  stroke: var(--border-color-base);
  stroke-width: 3.1;
  opacity: 0.58;
}
.wv-track-progress {
  stroke: var(--wv-role);
  stroke-width: 3.2;
  opacity: 0.92;
}

.wv-station {
  cursor: pointer;
  outline: none;
}

.wv-node {
  fill: var(--bg-body);
  stroke: var(--border-color-base);
  stroke-width: 2.2;
  transition:
    fill 0.42s cubic-bezier(0.22, 0.61, 0.36, 1),
    stroke 0.42s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.wv-station.reached .wv-node {
  fill: var(--wv-role);
  stroke: var(--wv-role);
}

.wv-node-label {
  fill: var(--text-color-alpha4);
  font-size: 14.5px;
  letter-spacing: 0.02em;
  transition: fill 0.42s ease;
}

.wv-station.reached .wv-node-label {
  fill: var(--wv-role);
}
.wv-station:focus-visible .wv-node {
  stroke-width: 4;
  stroke: var(--text-color);
}

.wv-atom {
  pointer-events: none;
}

.wv-atom.is-hidden {
  opacity: 0;
}
.wv-halo {
  fill: var(--wv-role);
  opacity: 0.14;
  animation: wv-breathe 3.4s ease-in-out infinite;
}
.wv-atom.wv-role-contractor .wv-halo {
  animation-delay: -1.7s;
}
.wv-ring {
  fill: none;
  stroke: var(--wv-role);
  stroke-width: 1.4;
  opacity: 0.58;
}
.wv-core {
  fill: var(--wv-role);
}
.wv-atom.waiting .wv-core {
  opacity: 0.48;
}
.wv-atom.waiting .wv-ring {
  stroke-dasharray: 2 4;
  opacity: 0.38;
}

@keyframes wv-breathe {
  0%,
  100% {
    r: 18;
    opacity: 0.14;
  }
  50% {
    r: 23;
    opacity: 0.06;
  }
}

.wv-rail-foot {
  padding: 16px;
  border-top: 1px solid var(--border-color-split);
  color: var(--text-color-alpha4);
  font-size: 10px;
  line-height: 1.65;
  letter-spacing: 0.04em;
}

@media (max-width: 1180px) {
  .wv-rail {
    grid-column: 1;
    grid-row: 1;
    border-right: 0;
    border-bottom: 1px solid var(--border-color-base);
  }
  .wv-rail-head { padding: 10px 16px; }
  .wv-rail-title { margin-bottom: 6px; }
  .wv-rail-foot { padding: 6px 12px; }
  .wv-rail-body { overflow: auto; min-height: 140px; }
  .wv-map {
    position: relative;
    top: 0;
    display: block;
    width: max(430px, 100%);
    height: var(--wv-map-height);
    margin: 0 auto;
  }
}

@media (prefers-reduced-motion: reduce) {
  .wv-node,
  .wv-node-label,
  .wv-track-progress,
  .wv-atom {
    transition: none;
  }
  .wv-halo {
    animation: none;
  }
}
</style>
